import { useState, useEffect } from "react";
import { contentfulClient } from "../services/api.ts";
import { useGlobalContext } from "../utils/GlobalContext.ts";
import { ContactUsForm } from "../graphql/graphql.ts";

const GetContactUsForm = `
  query GetContactUsForm($locale: String) {
    contactUsFormCollection(locale: $locale) {
      items {
        id
        field
        type
        order
        placeholder
        options
        required
        errorMessages
      }
    }
  }
`;

interface ContactUsFormResponse {
  contactUsFormCollection?: {
    items: ContactUsForm[];
  } | null;
}

interface ContactUsFormContent {
  formData: {
    items: ContactUsForm[];
  };
  loading: boolean;
  notFound: boolean;
  currentLocale: string | null;
}

export function useContactUsFormContent(): ContactUsFormContent {
  const { localeConfig } = useGlobalContext();
  const [items, setItems] = useState<ContactUsForm[]>([]);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!localeConfig.currentLocale) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    async function fetchFormContent() {
      try {
        setLoading(true);
        setNotFound(false);

        const response = await contentfulClient.query<ContactUsFormResponse>(
          GetContactUsForm,
          {
            locale: localeConfig.currentLocale ?? undefined,
          },
        );

        const formItems = response.contactUsFormCollection?.items ?? [];

        if (formItems.length === 0) {
          setNotFound(true);
          setItems([]);
          return;
        }

        setItems(formItems);
      } catch (error) {
        console.error("Error fetching contact us form content:", error);
        setNotFound(true);
        setItems([]);
      } finally {
        setLoading(false);
      }
    }

    fetchFormContent();
  }, [localeConfig, localeConfig.currentLocale]);

  return {
    formData: {
      items,
    },
    loading,
    notFound,
    currentLocale: localeConfig.currentLocale,
  };
}
